import { useEffect, useState } from 'react'
import { NextSeo } from 'next-seo'
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useRouter } from 'next/router'
import Link from 'next/link'
import axios from 'axios'
import {
  Box,
  Typography,
  Button,
  Sheet,
  Input,
  Divider,
  Stack,
  Checkbox,
} from '@mui/joy'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)

const pricing = {
  standard: { label: 'Standard Plan', amount: 59, original: 216 },
  pro: { label: 'Pro Plan', amount: 118, original: 348 },
  agency: { label: 'Agency Plan', amount: 177, original: 588 },
}

export default function CheckoutPage() {
  const router = useRouter()
  const { plan } = router.query

  const [clientSecret, setClientSecret] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!plan || typeof plan !== 'string') return
    if (!pricing[plan]) {
      setError('Invalid plan')
      return
    }

    axios
      .post('/api/ltd/create-payment-intent', {
        amount: pricing[plan].amount,
        plan,
      })
      .then((res) => setClientSecret(res.data.clientSecret))
      .catch(() => setError('Failed to create payment intent'))

  }, [plan])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
      if (!clientSecret || !plan || typeof plan !== 'string') return

      fetch('/api/facebook-capi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'ViewContent - LTD Checkout',
          event_source_url: window.location.href,
          content_name: `Scriptu ${pricing[plan].label}`,
          content_type: 'product',
          value: pricing[plan].amount,
          currency: 'USD',
          event_id: `viewcontent_${plan}_${clientSecret.split('_secret_')[0]}`,
        }),
      }).catch(console.error)
    }
  }, [clientSecret, plan])

  if (error) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography level="h4">Error</Typography>
        <Typography level="body1" color="danger">{error}</Typography>
      </Box>
    )
  }

  if (!clientSecret) {
    return (
      <Box textAlign="center" mt={8}>
        Loading checkout...
      </Box>
    )
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm plan={plan} clientSecret={clientSecret} />
    </Elements>
  )
}

function CheckoutForm({ plan, clientSecret }) {
  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  const handlePayment = async () => {
    if (!stripe || !elements) return
    if (!email || !firstName || !lastName) {
      setError('Please fill in all fields')
      return
    }

    setLoading(true)

    try {

      const checkRes = await axios.post('/api/ltd/check-existing', { email });

      if (checkRes.data.alreadyPaid) {
        setError('The account with email: ' + email + ' already has a lifetime deal. No need to purchase again.');
        setLoading(false);
        return;
      }
      const shortPaymentId = clientSecret.split('_secret_')[0];

      const saveRes = await axios.post('/api/ltd/save-user', {
        email,
        firstName,
        lastName,
        plan,
        stripePaymentId: shortPaymentId,
        amount: pricing[plan].amount,
      })

      if (!saveRes.data.success) {
        throw new Error('Failed to save user')
      }

      if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
        await fetch('/api/facebook-capi', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_name: 'InitiateCheckout Payment',
            event_source_url: window.location.href,
            email,
            first_name: firstName,
            last_name: lastName,
            value: pricing[plan].amount,
            currency: 'USD',
            event_id: `initcheckout_${plan}_${clientSecret.split('_secret_')[0]}`,
          }),
        }).catch(console.error)
      }


      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/success?plan=${plan}`,
        },
      })

      if (result.error) {
        setError(result.error.message || 'Payment failed')
      }

    } catch (err) {
      console.error('Payment Error:', err)
      setError('Payment failed. Please try again later.')
    }
    setLoading(false);
  }

  const planData = pricing[plan]
  const discount = planData.original - planData.amount

  return (
    <>
      <NextSeo
        title="Scriptu Checkout Lifetime Deal"
        description="Complete your purchase for Scriptu's lifetime deal. Enjoy AI-powered tools for LinkedIn content generation, scheduling, and optimization. Secure your lifetime access now!"
        canonical="https://www.scriptu.io/ltd" // Add your canonical URL here
        openGraph={{
          url: "https://www.scriptu.io/ltd", // It's also good to set the URL in openGraph
          title: "Scriptu Checkout Lifetime Deal",
          description: "Complete your purchase for Scriptu's lifetime deal. Enjoy AI-powered tools for LinkedIn content generation, scheduling, and optimization. Secure your lifetime access now!",
          images: [
            {
              url: "/logo-promo.png",
              width: 2000,
              height: 520,
              alt: "Scriptu Logo",
            },
          ],
          site_name: "Scriptu",
        }}
        twitter={{
          handle: '@handle',
          site: '@site',
          cardType: 'summary_large_image',
        }}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: '/favicon.png',
          },
          {
            rel: 'apple-touch-icon',
            href: '/favicon.png',
            sizes: '128x128',
          },
        ]}
      />

      <Box maxWidth={1000} mx="auto" display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4} mt={6}>
        <Box flex={1} sx={{ px: 3 }}>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              gap: 1.5,
              flexWrap: 'wrap', // handles small screens
            }}
          >
            <Link href="/ltd#pricing-section" passHref legacyBehavior>
              <Typography
                level="body2"
                component="a"
                sx={{ cursor: 'pointer', color: 'primary.plainColor', underline: 'none', fontWeight: 'md' }}
              >
                Pricing
              </Typography>
            </Link>

            <ArrowForwardIosIcon fontSize="inherit" sx={{ fontSize: 14, color: 'text.secondary' }} />

            <Typography level="body2" sx={{ fontWeight: 'md', color: 'text.primary' }}>
              Checkout
            </Typography>

            <ArrowForwardIosIcon fontSize="inherit" sx={{ fontSize: 14, color: 'text.secondary' }} />

            <Typography level="body2" sx={{ color: 'text.secondary' }}>
              Confirmation
            </Typography>
          </Box>


          <Typography level="h3" mb={1}>Secure Checkout</Typography>
          <Typography level="body1" mb={2}>Scriptu {planData.label} — Lifetime Access</Typography>

          <Stack spacing={2} mb={2}>
            <Input placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            <Input placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </Stack>

          <Typography level="h3" mb={1}>Payment Details</Typography>
          <Typography level="body2" mb={2}>Select your payment method below to complete the purchase.</Typography>
          <Sheet variant="outlined" sx={{ p: 2, borderRadius: 8, mb: 2 }}>
            <PaymentElement />
          </Sheet>

          {error && (
            <Box mb={2} sx={{ color: 'red', textAlign: 'center', border: '1px solid red', p: 2, borderRadius: 10 }}>
              <Typography level="body2">{error}</Typography>
            </Box>
          )}
          <Checkbox label="Get email updates about Scriptu.io" defaultChecked sx={{ mb: 2 }} />

          <Button onClick={handlePayment} loading={loading} fullWidth size="lg" disabled={!stripe || !elements}>
            Complete Purchase — ${planData.amount}
          </Button>
        </Box>

        <Box flexShrink={0} width={{ xs: '100%', md: 350 }} position={{ md: 'sticky' }} top={{ md: 40 }}>
          <Sheet variant="soft" sx={{ p: 3, borderRadius: 8 }}>
            <Typography level="h5" mb={1}>Order Summary</Typography>
            <Divider sx={{ my: 1 }} />

            <Typography level="body2">Scriptu {planData.label} Lifetime License</Typography>
            <Typography level="body2" sx={{ textDecoration: 'line-through' }}>${planData.original}</Typography>
            <Typography level="body2" fontWeight="lg">You Pay: ${planData.amount}</Typography>
            <Typography level="body2" color="success">You save ${discount}!</Typography>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              {[
                "30-Day Money-Back Promise, no questions asked",
                "No monthly fees, ever",
                "Access to all current and future features",
                "Lifetime updates included",
                "Trusted by 2,000+ professionals",
              ].map((text, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography level="body2" sx={{ mt: '2px', color: 'success.plainColor' }}>✔</Typography>
                  <Typography level="body2">{text}</Typography>
                </Box>
              ))}
            </Stack>
          </Sheet>
        </Box>
      </Box>
    </>
  )
}
